import { useState, useRef } from "react";
import { importApi } from "@/services/api";
import type { Contact, ImportResult } from "@/types";
import { Upload, Loader2, X } from "lucide-react";

interface VcfUploadProps {
  onImportSuccess: () => void;
}

export function VcfUpload({ onImportSuccess }: VcfUploadProps) {
  const [isUploading, setIsUploading] = useState(false);
  const [preview, setPreview] = useState<{ contacts: Contact[]; total_count: number } | null>(null);
  const [selectedContacts, setSelectedContacts] = useState<Set<number>>(new Set());
  const [isImporting, setIsImporting] = useState(false);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isCsvFile, setIsCsvFile] = useState(false);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setIsCsvFile(file.name.toLowerCase().endsWith('.csv'));

    setIsUploading(true);
    try {
      const content = await file.text();
      const response = isCsvFile
        ? await importApi.previewCsv(content)
        : await importApi.previewVcf(content);
      setPreview(response.data.data);
      setSelectedContacts(new Set(response.data.data.contacts.map((_, i) => i)));
    } catch (error) {
      console.error("Failed to preview file:", error);
      alert(isCsvFile ? "Fehler beim Lesen der CSV-Datei" : "Fehler beim Lesen der VCF-Datei");
    } finally {
      setIsUploading(false);
    }
  };

  const toggleContact = (index: number) => {
    const newSelected = new Set(selectedContacts);
    if (newSelected.has(index)) {
      newSelected.delete(index);
    } else {
      newSelected.add(index);
    }
    setSelectedContacts(newSelected);
  };

  const toggleAll = () => {
    if (selectedContacts.size === preview?.contacts.length) {
      setSelectedContacts(new Set());
    } else {
      setSelectedContacts(new Set(preview?.contacts.map((_, i) => i) || []));
    }
  };

  const handleImport = async () => {
    if (!preview || selectedContacts.size === 0) return;

    setIsImporting(true);
    try {
      const contactsToImport = Array.from(selectedContacts).map(i => preview.contacts[i]);
      const response = isCsvFile
        ? await importApi.importCsv(contactsToImport)
        : await importApi.importVcf(contactsToImport);
      setImportResult(response.data.data);
      setPreview(null);
      setSelectedContacts(new Set());
      onImportSuccess();
    } catch (error) {
      console.error("Failed to import:", error);
      alert("Fehler beim Importieren");
    } finally {
      setIsImporting(false);
    }
  };

  const closeResults = () => {
    setImportResult(null);
  };

  const fileAccept = ".vcf,.csv";
  const fileLabel = "VCF/CSV-Datei hochladen";

  if (importResult) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-medium text-gray-900">Importergebnis</h3>
          <button
            onClick={closeResults}
            className="p-1 hover:bg-gray-100 rounded"
          >
            <X className="h-4 w-4 text-gray-500" />
          </button>
        </div>

        <p className="text-sm text-gray-700">
          {importResult.success_count} von {importResult.total} Kontakten eingereicht
        </p>

        {importResult.imported.length > 0 && (
          <div className="max-h-64 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="text-left px-2 py-1 font-medium text-gray-700">Kontakt</th>
                  <th className="text-left px-2 py-1 font-medium text-gray-700">Aktion</th>
                  <th className="text-left px-2 py-1 font-medium text-gray-700">Grund</th>
                  <th className="text-left px-2 py-1 font-medium text-gray-700">Status</th>
                </tr>
              </thead>
              <tbody>
                {importResult.imported.map((item, idx) => (
                  <tr key={idx} className="border-b border-gray-100">
                    <td className="px-2 py-2 text-gray-900">{item.contact}</td>
                    <td className="px-2 py-2 text-gray-700">{item.action}</td>
                    <td className="px-2 py-2 text-gray-600">{item.reason}</td>
                    <td className="px-2 py-2 text-green-600">{item.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {importResult.errors.length > 0 && (
          <div className="space-y-2">
            <h4 className="font-medium text-red-700 text-sm">Fehler</h4>
            <div className="max-h-32 overflow-y-auto space-y-1">
              {importResult.errors.map((err, idx) => (
                <div key={idx} className="text-sm text-red-600 bg-red-50 p-2 rounded">
                  <span className="font-medium">{err.contact}:</span> {err.error}
                </div>
              ))}
            </div>
          </div>
        )}

        <button
          onClick={closeResults}
          className="w-full px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
        >
          Schließen
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {!preview ? (
        <div
          onClick={() => fileInputRef.current?.click()}
          className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:border-primary-600 hover:bg-primary-50 transition-colors"
        >
          <input
            ref={fileInputRef}
            type="file"
            accept={fileAccept}
            onChange={handleFileSelect}
            className="hidden"
          />
          {isUploading ? (
            <div className="flex flex-col items-center">
              <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
              <p className="mt-2 text-sm text-gray-600">Datei wird analysiert...</p>
            </div>
          ) : (
            <div className="flex flex-col items-center">
              <Upload className="h-8 w-8 text-gray-400" />
              <p className="mt-2 text-sm font-medium text-gray-900">{fileLabel}</p>
              <p className="text-xs text-gray-500">Klicken oder Datei hierher ziehen</p>
            </div>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-medium text-gray-900">Vorschau</h3>
              <p className="text-sm text-gray-600">
                {preview.total_count} Kontakte gefunden
              </p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => { setPreview(null); setSelectedContacts(new Set()); }}
                className="px-3 py-2 text-sm text-gray-600 hover:text-gray-900"
              >
                Abbrechen
              </button>
              <button
                onClick={handleImport}
                disabled={selectedContacts.size === 0 || isImporting}
                className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 flex items-center"
              >
                {isImporting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Importieren...
                  </>
                ) : (
                  `${selectedContacts.size} Kontakte importieren`
                )}
              </button>
            </div>
          </div>

          <div className="flex items-center gap-2 mb-2 pb-2 border-b">
            <input
              type="checkbox"
              checked={selectedContacts.size === preview.contacts.length}
              onChange={toggleAll}
              className="rounded border-gray-300"
            />
            <span className="text-sm font-medium">Alle auswählen</span>
          </div>

          <div className="max-h-64 overflow-y-auto space-y-2">
            {preview.contacts.map((contact, index) => (
              <div
                key={index}
                className={`flex items-center gap-3 p-2 rounded ${selectedContacts.has(index) ? "bg-primary-50" : "hover:bg-gray-50"}`}
              >
                <input
                  type="checkbox"
                  checked={selectedContacts.has(index)}
                  onChange={() => toggleContact(index)}
                  className="rounded border-gray-300"
                />
                <div className="flex-1">
                  <p className="font-medium text-gray-900">
                    {contact.display_name || contact.first_name + " " + contact.last_name}
                  </p>
                  {contact.email_address && (
                    <p className="text-sm text-gray-600">{contact.email_address}</p>
                  )}
                  {contact.company_name && (
                    <p className="text-xs text-gray-500">{contact.company_name}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
